import classNames from "classnames";
import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";

import { parseDuration } from "../../utils/duration";

import styles from "./highlight.css";

import type { PropertyValueMap } from "lit";

// Initially no highlight, then highlight rolls to right
// Instantly transition back to "initHidden" after "hiddenAfterShown" transition plays
type showState = "hiddenAfterShown" | "initHidden" | "shown";

@customElement("bp-highlight")
export class Highlight extends LitElement {
  static styles = styles;

  @property({ type: Boolean, attribute: "can-spotlight" })
  canSpotlight?: boolean;

  @property({ attribute: "color" })
  color?: string = "yellow";

  lastState?: showState;

  @property({
    type: Boolean,
    attribute: "no-animation",
  })
  noAnimation?: boolean = false;

  resetStateTimeoutId: NodeJS.Timeout | null = null;

  revealTime = 1; // Sometimes we override user time to 0 to allow for reset
  shouldResetState = false; // True to allow the highlight state to reset
  @property({
    type: Boolean,
    attribute: "show",
  })
  show?: boolean = false;
  shownState: showState = "initHidden";
  @property({ type: Number, attribute: "reveal-time" })
  userRevealTime?: number = 1;

  render() {
    const classes = classNames("highlight", this.color, this.shownState, {
      "can-spotlight": this.canSpotlight,
    });
    const style = `--reveal-time: calc(${this.revealTime} * var(--duration-x-long))`;

    return html`<span class="${classes}" style="${style}"><slot></slot></span>`;
  }

  resetShownState() {
    const baseTimeoutDuration = parseDuration(
      globalThis.window
        .getComputedStyle(this)
        .getPropertyValue("--duration-x-long"),
    );
    const timeoutDuration = this.revealTime * baseTimeoutDuration + 50;
    this.resetStateTimeoutId = setTimeout(() => {
      this.shouldResetState = true;
      this.requestUpdate();
    }, timeoutDuration);
  }

  updated() {
    this.lastState = this.shownState;
  }

  protected willUpdate(
    changedProperties: Map<PropertyKey, unknown> | PropertyValueMap<this>,
  ): void {
    if (changedProperties.has("show")) {
      this.shouldResetState = false; // Cancel reset when state changes again;

      if (this.resetStateTimeoutId) {
        clearTimeout(this.resetStateTimeoutId);
      }
    }

    if (this.shouldResetState) {
      this.revealTime = 0;
      this.shownState = "initHidden";
      this.shouldResetState = false;

      return;
    }

    if (this.noAnimation) {
      this.revealTime = 0;
    } else if (this.userRevealTime) {
      this.revealTime = this.userRevealTime;
    }
    this.shownState = this.show
      ? "shown"
      : this.lastState === "shown"
        ? "hiddenAfterShown"
        : "initHidden";

    if (this.shownState === "hiddenAfterShown") {
      this.resetShownState();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-highlight": Highlight;
  }
}
